/* eslint-disable import/no-named-as-default */
'use client'

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@components/ui/form'
import { Select, SelectTrigger, SelectValue } from '@components/ui/select'
import {
  SelectCountryContent,
  zodCountryOptions,
} from '@components/form/select-country'
import {
  SelectIndustryContent,
  zodIndustryOptions,
} from '@components/form/select-industry'
import { type GatedContentResponse } from '@app/api/gated-content/route'
import { type GatedContentRequest } from '@lib/types'

import FormTermsAndConditions from '@components/form/terms-and-conditions'
import { Input } from '@components/ui/input'
import { Switch } from '@components/ui/switch'
import { Toaster } from '@components/ui/sonner'
import { toast } from 'sonner'
import { useForm } from 'react-hook-form'
import { useUtmInitialValues } from '@components/form/utm-fields'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import Button from '@components/interaction/button'
import { useEffect, useState } from 'react'
import { useSearchParams } from 'next/navigation'
import logger from '@config/logger'
import { executeRecaptcha } from '@lib/recaptcha-client'
import { MailCheck } from 'lucide-react'
import Recaptcha from '@components/form/recaptcha'
import { linkedInTrack } from '@lib/linkedin'
import { sendGTMEvent } from '@next/third-parties/google'
import { FormEvent, type PosthogPerson, StandardEvent } from '@lib/analytics'
import posthog from 'posthog-js'

const formSchema = z.object({
  firstName: z.string().min(2, 'First name is required').max(50),
  lastName: z.string().min(2, 'Last name is required').max(50),
  email: z.string().email(),
  industry: zodIndustryOptions,
  jobTitle: z.string().min(2, 'Job title is required').max(50),
  company: z.string().min(2, 'Company name is required').max(50),
  country: zodCountryOptions,
  subscribe: z.boolean(),
})

// eslint-disable-next-line sonarjs/cognitive-complexity
export default function DownloadForm({
  slug,
  disabled,
  linkedInConversionId,
}: {
  slug: string
  disabled: boolean
  linkedInConversionId?: number | null
}) {
  const utmInitialValues = useUtmInitialValues()
  const [loadRecaptcha, setLoadRecaptcha] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [downloadSucceeded, setDownloadSucceeded] = useState(false)
  const [downloadResponse, setDownloadResponse] = useState<string | null>(null)
  const searchParams = useSearchParams()
  const debug = searchParams.get('debug')

  useEffect(() => {
    if (downloadResponse) {
      // Pardot returns a HTTP 302 meta refresh, instead of a Location header... 🤦‍♂️
      // Our API returns the Pardot meta refresh HTML document for the FE to parse, because HTML parsing is not as easy on the backend.
      try {
        processDownloadResponse(downloadResponse)
        setDownloadSucceeded(true)
      } catch (error) {
        if (debug === 'true') {
          logger.debug({
            message: 'Download response',
            data: downloadResponse,
            error,
          })
        }
        toast.error('Failed to open Download', {
          description: 'Please check your email for the download instead!',
          action: {
            label: 'Close',
            onClick: () => null,
          },
        })
        setDownloadResponse(null)
      }
    }
  }, [downloadResponse, debug])

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      industry: '',
      jobTitle: '',
      company: '',
      country: '',
      subscribe: true,
    },
  })

  async function onSubmit(values: z.infer<typeof formSchema>) {
    setSubmitting(true)

    try {
      const token = await executeRecaptcha(FormEvent.GatedContentDownload)

      if (!token) {
        toast.error('Failed to Send', {
          description: 'We could not verify that you are not a bot.',
          action: {
            label: 'Close',
            onClick: () => null,
          },
        })
        return
      }

      const person: PosthogPerson = values
      posthog.identify(values.email, person)

      const data: GatedContentRequest = {
        ...utmInitialValues,
        ...values,
        slug,
        token,
      }

      if (debug === 'true') {
        toast('Debug: GatedContentRequest', {
          description: (
            <pre className='mt-2 max-w-80 overflow-x-scroll rounded-md bg-slate-950 p-4'>
              <code className='text-white'>
                {JSON.stringify(data, null, 2)}
              </code>
            </pre>
          ),
        })
      }

      const html = await postDownladForm(data)
      linkedInTrack(linkedInConversionId)
      posthog.capture(StandardEvent.GenerateLead, {
        channel: FormEvent.GatedContentDownload,
        linkedInConversionId,
        industry: data.industry,
        subscribe: data.subscribe,
        ...utmInitialValues,
      })
      sendGTMEvent({ event: StandardEvent.GenerateLead })
      setDownloadResponse(html)
    } catch (error) {
      if (debug === 'true') {
        logger.error(error)
      }
      toast.error('Failed to Send', {
        description: 'Something went wrong. Please try again.',
        action: {
          label: 'Close',
          onClick: () => null,
        },
      })
    }
    setSubmitting(false)
  }

  return disabled ? (
    <div className='mx-auto flex min-w-80 items-center bg-gold p-10 lg:max-w-xl lg:shadow-md xl:p-12'>
      <h2 className='mx-auto text-xl font-bold'>Download unavailable</h2>
    </div>
  ) : (
    <Form {...form}>
      {loadRecaptcha && <Recaptcha />}
      <form
        id={FormEvent.GatedContentDownload}
        onSubmit={form.handleSubmit(onSubmit)}
        className='mx-auto h-fit bg-gold p-5 sm:p-8 md:max-w-2xl lg:max-w-xl lg:p-10 lg:shadow-md xl:p-12'
      >
        {downloadSucceeded ? (
          <div className='h-full w-full flex-col items-center justify-center text-center lg:w-96'>
            <div className='mb-4 w-full'>
              <MailCheck className='mx-auto h-10 w-10 stroke-zinc-700' />
            </div>

            <span className='text-2xl text-zinc-700 sm:text-h8 lg:text-sub-title'>
              Download requested!
            </span>
          </div>
        ) : (
          <>
            <h2 className='mb-5 text-2xl font-bold sm:text-h8'>
              Download your copy
            </h2>
            <div className='grid grid-cols-1 gap-4 md:grid-cols-2 lg:gap-5'>
              <FormField
                control={form.control}
                name='firstName'
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        autoComplete='given-name'
                        placeholder='First name *'
                        onFocus={() => {
                          setLoadRecaptcha(true)
                        }}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name='lastName'
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        autoComplete='family-name'
                        placeholder='Last name *'
                        onFocus={() => {
                          setLoadRecaptcha(true)
                        }}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name='email'
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        autoComplete='email'
                        placeholder='Email *'
                        onFocus={() => {
                          setLoadRecaptcha(true)
                        }}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name='industry'
                render={({ field }) => (
                  <FormItem>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue
                            placeholder='Industry *'
                            onFocus={() => {
                              setLoadRecaptcha(true)
                            }}
                          />
                        </SelectTrigger>
                      </FormControl>
                      <SelectIndustryContent />
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name='jobTitle'
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        autoComplete='organization-title'
                        placeholder='Job title *'
                        onFocus={() => {
                          setLoadRecaptcha(true)
                        }}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name='company'
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        autoComplete='organization'
                        placeholder='Company name *'
                        onFocus={() => {
                          setLoadRecaptcha(true)
                        }}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name='country'
                render={({ field }) => (
                  <FormItem>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue
                            placeholder='Country *'
                            onFocus={() => {
                              setLoadRecaptcha(true)
                            }}
                          />
                        </SelectTrigger>
                      </FormControl>
                      <SelectCountryContent />
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name='subscribe'
              render={({ field }) => (
                <FormItem className='mx-2 my-6 flex flex-row items-center space-x-3'>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      onFocus={() => {
                        setLoadRecaptcha(true)
                      }}
                    />
                  </FormControl>
                  <FormLabel>
                    I would like to receive marketing communications regarding
                    V2 insights, services and events.
                  </FormLabel>
                </FormItem>
              )}
            />
            <Button
              disabled={submitting || downloadSucceeded}
              variant='contained'
              size='md'
              loading={submitting}
              colour='secondary'
              type='submit'
            >
              {submitting ? 'Requesting...' : 'Download'}
            </Button>
            <FormTermsAndConditions submitButtonLabel='Download' />
          </>
        )}
      </form>
      <Toaster />
    </Form>
  )
}

const processDownloadResponse = (html: string) => {
  // Get the URL from the meta refresh tag e.g. <meta http-equiv="refresh" content="0;url=https://consult.v2.digital/some.pdf">
  const downloadUrl = new DOMParser()
    .parseFromString(html, 'text/html')
    .querySelector('meta[http-equiv="refresh"]')
    ?.getAttribute('content')
    ?.split(';')[1]
    .split('=')[1]

  if (downloadUrl) {
    const link = document.createElement('a')
    link.href = downloadUrl
    link.setAttribute(
      'download',
      downloadUrl.split('/').pop() ?? 'V2-Digital-Download.pdf',
    )
    link.setAttribute('rel', 'noopener noreferrer')
    link.setAttribute('target', 'about:blank')

    document.body.append(link)
    link.click()
    link.remove()
  } else {
    throw new Error('Download URL not found in response.')
  }
}

const postDownladForm = async (data: GatedContentRequest): Promise<string> => {
  const response = await fetch('/api/gated-content', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })
  const responseData: GatedContentResponse = await response.json()

  if (response.ok) {
    toast('Download request submitted', {
      description: responseData.message,
      action: {
        label: 'Close',
        onClick: () => null,
      },
    })

    if (responseData.html) {
      return responseData.html
    }

    throw new Error('Download response HTML not found in response.')
  } else {
    throw new Error(responseData.message)
  }
}
